import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Modal from '@/components/ui/Modal';
import { BannerOverlayPosition } from '@/types/ads';
import { resolveTextAdFontFamily } from '@/constants/textAdFonts';

type TextPreviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  text: string;
  fontSize: number;
  fontColor: string;
  fontFamily?: string;
  backgroundColor: string;
  tickerSpeedSeconds: number;
  position: BannerOverlayPosition;
};

const TextPreviewModal = ({
  isOpen,
  onClose,
  returnFocusRef,
  text,
  fontSize,
  fontColor,
  fontFamily,
  backgroundColor,
  tickerSpeedSeconds,
  position,
}: TextPreviewModalProps) => {
  const normalizeColor = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      return trimmed;
    }
    return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  };

  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    setContainerEl(node);
  }, []);
  const rafRef = useRef<number | null>(null);
  const [scale, setScale] = useState(0);
  const baseWidth = 1920;
  const baseHeight = 1080;
  const spScale = 1.6;
  const scaledFontSize = fontSize * spScale;
  const paddingX = Math.max(24, Math.round(fontSize * 0.6));
  const paddingY = Math.max(12, Math.round(scaledFontSize * 0.2));
  const lineHeight = 1.1;
  const tickerHeight = Math.ceil(scaledFontSize * lineHeight + paddingY * 2);
  const laneBottomOffset = Math.max(12, Math.round(scaledFontSize * 0.24));
  const resolvedFontColor = normalizeColor(fontColor);
  const resolvedBackgroundColor = normalizeColor(backgroundColor);
  const resolvedFontFamily = resolveTextAdFontFamily(fontFamily);

  const updateScale = useCallback((container?: HTMLDivElement | null) => {
    const target = container ?? containerEl;
    if (!target) {
      return false;
    }
    const { width, height } = target.getBoundingClientRect();
    if (width === 0 || height === 0) {
      return false;
    }
    const nextScale = Math.min(width / baseWidth, height / baseHeight);
    setScale(nextScale > 0 ? nextScale : 1);
    return true;
  }, [baseHeight, baseWidth, containerEl]);

  useLayoutEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    let active = true;
    const measure = () => {
      if (!active) return;
      const ready = updateScale();
      if (!ready) {
        rafRef.current = requestAnimationFrame(measure);
      }
    };
    rafRef.current = requestAnimationFrame(measure);
    return () => {
      active = false;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isOpen, updateScale]);

  useEffect(() => {
    if (!isOpen || !containerEl) {
      return undefined;
    }

    if (typeof ResizeObserver === 'undefined') {
      const handleResize = () => updateScale(containerEl);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }

    const observer = new ResizeObserver(() => updateScale(containerEl));
    observer.observe(containerEl);
    return () => observer.disconnect();
  }, [containerEl, isOpen, updateScale]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Vista previa en TV"
      size="xl"
      returnFocusRef={returnFocusRef}
    >
      <style>
        {`
          @keyframes ticker-scroll {
            0% { transform: translateX(100%); }
            100% { transform: translateX(-100%); }
          }
        `}
      </style>
      <div className="space-y-4">
        <div className="text-sm text-gray-500">
          Simulación de un ticker de noticias. El texto se desplaza de derecha a izquierda.
        </div>
        <div
          ref={containerRef}
          className="relative w-full aspect-video overflow-hidden border border-gray-200 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 shadow-inner"
        >
          <div
            className="absolute left-0 top-0 origin-top-left"
            style={{
              width: `${baseWidth}px`,
              height: `${baseHeight}px`,
              transform: `scale(${scale})`,
            }}
          >
            <div
              className={`absolute left-0 right-0 z-10 ${position === 'TOP' ? 'top-0' : ''}`}
              style={{
                backgroundColor: resolvedBackgroundColor,
                height: `${tickerHeight}px`,
                bottom: position === 'BOTTOM' ? `${laneBottomOffset}px` : undefined,
              }}
            >
              <div
                className="h-full overflow-hidden flex items-center"
                style={{ padding: `${paddingY}px ${paddingX}px` }}
              >
                <div
                  className="whitespace-nowrap"
                  style={{
                    color: resolvedFontColor,
                    fontSize: `${scaledFontSize}px`,
                    lineHeight: `${scaledFontSize * lineHeight}px`,
                    fontFamily: resolvedFontFamily,
                    animation: `ticker-scroll ${tickerSpeedSeconds}s linear infinite`,
                  }}
                >
                  {text || 'Escribe el texto del anuncio para previsualizarlo.'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TextPreviewModal;
